import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	activateNews,
	createNews,
	desactivateNews,
	getNewsById,
	getOlibsLibrariesCodes,
	updateNewsById,
} from '../../Services/news';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { NewsSchema } from './formValidation';
import { toast } from 'react-toastify';

export const useNewsDetailPage = () => {
	const { id } = useParams();
	const [richtext, setRichtext] = useState('');
	const [variant, setVariant] = useState('create' | 'edit');
	const [olibLibraries, setOlibLibraries] = useState([]);
	const [allLibraries, setAllLibraries] = useState([]);
	const navigate = useNavigate();
	const AllLibrariesOption = { label: 'INCLUDE ALL', value: 'ALL' };
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [enabled, setEnabled] = useState(false);

	// Forms
	const {
		setValue,
		control,
		handleSubmit,
		formState: { errors },
		getValues,
	} = useForm({
		resolver: zodResolver(NewsSchema),
		defaultValues: {
			title: '',
			description: '',
			rich_text: richtext,
			image: null,
			institution_codes: [],
		},
	});

	const containsAll = (items) => items.some(({ value }) => value === 'ALL');

	const handleGetOlibLibraries = async () => {
		try {
			const response = await getOlibsLibrariesCodes();

			const responseOptions = response?.data?.map((item) => {
				return {
					value: item,
					label: item,
				};
			});

			setOlibLibraries([AllLibrariesOption, ...responseOptions]);
			setAllLibraries([AllLibrariesOption, ...responseOptions]);
		} catch (error) {
			console.error('Error fetching OLIB libraries:', error);
		}
	};

	const handleChangeSelectInstitution = (selectedOptions, field) => {
		const isAllSelected = containsAll(selectedOptions);
		field.onChange(selectedOptions);

		if (isAllSelected) {
			const AllLibrariesOptionsWithoutAll = allLibraries
				?.filter((item) => {
					return {
						label: item.label,
						value: item.value,
					};
				})
				.filter((item) => item.value !== 'ALL');

			setOlibLibraries([AllLibrariesOption]);
			setValue('institution_codes', AllLibrariesOptionsWithoutAll);
			return;
		}

		setOlibLibraries(allLibraries);
	};

	const onSubmit = async (data) => {
		const idLoading = toast.loading(
			`${variant === 'create' ? 'Creating' : 'Updating'} News`,
			{
				closeButton: true,
				autoClose: 1000,
			}
		);
		try {
			const {
				title,
				rich_text: richText,
				description,
				institution_codes,
				image,
			} = data;

			const imageFile = image instanceof FileList ? image[0] : undefined;

			const payload = {
				title,
				richText,
				description,
				institutionCodes: institution_codes.map((o) => o.value),
				...(imageFile && { imageFile }),
				...(variant === 'edit' && { newsId: id }),
			};
			setIsSubmitting(true);

			const result =
				variant === 'create'
					? await createNews(payload)
					: await updateNewsById(payload);

			toast.update(idLoading, {
				render: `${
					variant === 'create' ? 'News created' : 'News updated'
				} successfully!`,
				type: 'success',
				isLoading: false,
				closeButton: true,
				autoClose: 1000,
			});

			if (result.news_id) {
				navigate(`/news/${result.news_id}`);
			}
		} catch (err) {
			console.error('Save failed:', err);
			toast.update(idLoading, {
				render: 'Error, try again.',
				type: 'error',
				isLoading: false,
				closeButton: true,
				autoClose: 1000,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleGetNewsById = async (id) => {
		try {
			const news = await getNewsById(id);
			const institutionCodes = news?.institutions?.map((code) => ({
				value: code,
				label: code,
			}));
			setEnabled(news?.is_active);

			setValue('title', news?.title);
			setValue('description', news?.description);
			setValue('rich_text', news?.rich_text);
			setValue('image', news?.image_url ? news.image_url : null);
			setValue('institution_codes', institutionCodes);
		} catch (error) {
			console.error('Error fetching news by ID:', error);
		}
	};

	const handleActivateNews = async (newsId) => {
		try {
			await activateNews(newsId);
			handleGetNewsById(newsId);
			toast.success('News activated');
		} catch (error) {
			toast.error('Error activating news');
			console.error('Error activating news:', error);
		}
	};

	const handleDesactivateNews = async (newsId) => {
		try {
			await desactivateNews(newsId);
			handleGetNewsById(newsId);
			toast.success('News deactivated');
		} catch (error) {
			toast.error('Error deactivating news');
			console.error('Error deactivating news:', error);
		}
	};

	useEffect(() => {
		handleGetOlibLibraries();
	}, []);

	useEffect(() => {
		if (id) {
			setVariant(id === '0' ? 'create' : 'edit');
		}
	}, [id]);

	useEffect(() => {
		if (variant === 'edit' && id) {
			handleGetNewsById(id);
		}
	}, [variant, id]);

	useEffect(() => {
		if (!localStorage.name || !localStorage.email) {
			navigate('/');
		}
	}, []);

	return {
		richtext,
		setRichtext,
		navigate,
		olibLibraries,
		variant,
		handleChangeSelectInstitution,
		control,
		errors,
		onSubmit,
		handleSubmit,
		getValues,
		isSubmitting,
		enabled,

		id,
		handleActivateNews,
		handleDesactivateNews,
	};
};
